import { Typography } from "@mui/material";
import { ComponentProps, ElementType, useEffect, useRef, useState } from "react";

type AnimatedNumberProps<T extends ElementType = typeof Typography> = {
  animateOnMount?: boolean;
  component?: T;
  decimalPlaces?: number;
  durationMs?: number;
  easing?: (progress: number) => number;
  formatValue?: (value: number) => string;
  value: number;
} & Omit<ComponentProps<T>, "children" | "component">;

const easeOutCubic = (progress: number) => 1 - (1 - progress) ** 3;

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const roundToDecimalPlaces = (value: number, decimalPlaces: number) => {
  const multiplier = 10 ** decimalPlaces;

  return Math.round(value * multiplier) / multiplier;
};

export default function AnimatedNumber<T extends ElementType = typeof Typography>({
  animateOnMount = false,
  component,
  decimalPlaces = 0,
  durationMs = 650,
  easing = easeOutCubic,
  formatValue = String,
  value,
  ...props
}: AnimatedNumberProps<T>) {
  const previousValueRef = useRef(value);
  const hasMountedRef = useRef(false);
  const [displayValue, setDisplayValue] = useState(value);
  const Component = component ?? Typography;

  useEffect(() => {
    const fromValue = previousValueRef.current;
    previousValueRef.current = value;

    if (fromValue === value) return;

    if (
      prefersReducedMotion() ||
      durationMs <= 0 ||
      (!animateOnMount && !hasMountedRef.current)
    ) {
      setDisplayValue(value);
      hasMountedRef.current = true;
      return;
    }

    let animationFrameId = 0;
    const startTime = performance.now();

    const tick = (currentTime: number) => {
      const elapsedMs = currentTime - startTime;
      const progress = Math.min(elapsedMs / durationMs, 1);
      const easedProgress = easing(progress);

      setDisplayValue(fromValue + (value - fromValue) * easedProgress);

      if (progress < 1) {
        animationFrameId = requestAnimationFrame(tick);
        return;
      }

      setDisplayValue(value);
    };

    animationFrameId = requestAnimationFrame(tick);
    hasMountedRef.current = true;

    return () => cancelAnimationFrame(animationFrameId);
  }, [animateOnMount, durationMs, easing, value]);

  return (
    <Component {...props}>
      {formatValue(roundToDecimalPlaces(displayValue, decimalPlaces))}
    </Component>
  );
}
